/* LicensingComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./LicensingRenderer");

/**
 * @class LicensingComponent
 * @extends EventEmitter
 *
 * @fires LicensingComponent#Organization
 * @fires LicensingComponent#SystemInfo
 * @fires LicensingComponent#Shutdown
 */
class LicensingComponent extends EventEmitter {
    /**
     *
     * @param {UserFeedbackComponent} userFeedback
     * @param {UserSession} userSession
     * @param {LicenseEntity.} LicenseEntity
     * @param {LicenseStateEntity.} LicenseStateEntity
     * @param {Requestolizer} systemService
     */
    constructor(userFeedback, userSession, LicenseEntity, LicenseStateEntity, systemService, licenseRestriction) {
        super();

        this.systemService = systemService;
        this._userFeedback = userFeedback;
        this._orgId = _.get(userSession, "activeOrganization.id");
        this._LicenseEntity = LicenseEntity;
        this._LicenseStateEntity = LicenseStateEntity;
        this._licenseRestriction = licenseRestriction;
        this._user = userSession.getUser();

        this._establishSummaryFeed(this._orgId);
        this._establishLicenseFeed(this._orgId);

        this.requestSystemInfo();

        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    get canAddLicense() {
        return this._user.isAuthorizedForAction('licenseWrite');
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer && this._renderer.getRootElement();
    }

    get userFeedback() {
        return this._userFeedback;
    }

    get orgId() {
        return this._orgId;
    }

    get systemInfo() {
        return this._systemInfo;
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _shutdown() {
        this._triggerShutdown();
        this._unregisterListeners();
        return Promise.all([
            this._renderer.shutdown(),
            this.summaryFeed.shutdown(),
            this.licenseFeed.shutdown(),
        ])
            .then(() => {
                this._renderer = null;
                this._shutdownProm = null;
                this._listeners = null;
                this._orgId = null;
                this.summaryFeed = null;
                this.licenseFeed = null;
                this.removeAllListeners();
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    _triggerOrganization(org) {
        /**
         * @event LicensingComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit('Organization', org);
    }

    _triggerSystemInfo(systemInfo) {
        /**
         * @event LicensingComponent#SystemInfo
         * @param {{systemName: string, token: string}}
         */
        this.emit("SystemInfo", systemInfo);
    }

    _triggerShutdown() {
        /**
         * @event LicensingComponent#Shutdown
         */
        this.emit("Shutdown");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    /**
     * establish or reestablish summaryFeed
     * @private
     */
    _establishSummaryFeed(orgId) {
        if (!orgId) {
            return;
        }
        const query = { /*error: null*/ };
        const fields = {
            uniqueProp: false
        };
        if (this.summaryFeed) {
            this.summaryFeed.reestablishFeed(orgId, query, fields);
        } else {
            this.summaryFeed = this._LicenseStateEntity.startAscendLicenseStatesFeed(orgId, query, fields);
        }
        return this.summaryFeed;
    }
    _establishLicenseFeed(orgId) {
        if (!orgId) {
            return;
        }
        const query = {invalid: {$ne: true}};
        const fields = [
            "activeDate",
            "contractId",
            "deviceCount",
            "expireDate",
            "licenseKey",
            "solutionKey",
            "expired"
        ].reduce((fields, field) => Object.assign(fields, {[field]: true}), {});
        const sort = {"createdOn": 1};

        if (this.licenseFeed) {
            this.licenseFeed.reestablishFeed(orgId, query, fields, sort);
        } else {
            this.licenseFeed = this._LicenseEntity.startAscendLicensesFeed(orgId, query, fields, sort);
        }

        return this.licenseFeed;
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    organizationChanged({ id, name }) {
        if (this._orgId === id) {
            return;
        }

        this._orgId = id;
        this._establishSummaryFeed(id);
        this._establishLicenseFeed(id);
        this._triggerOrganization({id, name});
    }

    requestSystemInfo() {
        this.systemService.getToken({}).then(this.setSystemInfo.bind(this));
    }

    setSystemInfo(systemInfo) {
        this._systemInfo = systemInfo;
        this._triggerSystemInfo(systemInfo);
    }

    previewLicense(key) {
        const encryptedData = String(key).replace(/\s/g, "");
        return this._LicenseEntity.previewLicenseData(this._orgId, encryptedData).catch((error) => {
            console.error("previewLicense Error", error);
            return {
                errorMsg: (error && error.errorMsg) || "Request Error"
            };
        });
    }

    addLicense(key) {
        const encryptedData = String(key).replace(/\s/g, "");
        return this._LicenseEntity.addLicenseData(this._orgId, encryptedData)
            .then((licenseData) => {
                if (!licenseData.errorMsg) {
                    // This will handle the case where a new license is added
                    // to a child and the license feed needs to be updated
                    // because the license feed is no longer coming from the parent
                    // feed.  Case 24515
                    this._licenseRestriction.reloadLicenseData(this._orgId);
                    let holdOrgId = this._orgId;
                    this._orgId = null;
                    this.organizationChanged({id: holdOrgId});
                }
                return licenseData;
            });
    }

    deleteLicense(licenseItem) {
        licenseItem.setDeleted(true);
        return licenseItem.update();
    }

    getLicensesByKey(licenseKey) {
        return this._LicenseEntity
            .loadByQuery(this._orgId, this._LicenseEntity.getEntityType(), {
                licenseKey: licenseKey
            })
            .catch((error) => {
                console.error("getLicensesByKey Error", error);
                return [];
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

LicensingComponent.$groupName = "Organization";
LicensingComponent.$displayName = "Licensing";
LicensingComponent.$inject = [
    "$teve.ui.userFeedback",
    "$teve.userSession",
    "$teve.entities.License",
    "$teve.entities.LicenseState",
    "$teve.services.System",
    "$teve.licenseRestriction"
];
module.exports = LicensingComponent;
